/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Sep 12, 2026 by hoarfroster
 *
 */

import { useCallback, useMemo, type ReactNode } from 'react';
import { createContext } from 'react';
import { useToast } from '@components/ui/feedback';

export type BannerMessage = { message: string; type: 'error' | 'success' | 'info' } | null;

export const BannerContext = createContext<{ message: BannerMessage; setMessage: (msg: BannerMessage) => void }>({
    message: null,
    // Provide a safe default that surfaces misuse instead of an empty function
    setMessage: (msg: BannerMessage) => console.warn('BannerContext.setMessage called before provider is mounted', msg),
});

/**
 * Bridges the legacy BannerContext API onto the unified toast queue so existing
 * call sites keep working; prefer `useToast()` in new code.
 */
export function BannerProvider({ children }: { children: ReactNode }) {
    const toast = useToast();

    const setMessage = useCallback(
        (msg: BannerMessage) => {
            if (msg) toast.show(msg.message, msg.type);
        },
        [toast]
    );

    const value = useMemo(() => ({ message: null as BannerMessage, setMessage }), [setMessage]);

    return <BannerContext.Provider value={value}>{children}</BannerContext.Provider>;
}
